import { useTransitionGuards } from "./useTransitionGuards";
import type { Match } from "../types";
import type { PeriodPhase } from "./usePeriodManager";

interface UseCockpitTransitionStateParams {
  statusOverride?: Match["status"];
  currentPhase: PeriodPhase;
  globalTimeSeconds: number;
  match: Match | null;
  isAdmin: boolean;
  t: (key: string, fallback: string) => string;
  /**
   * True while `usePeriodManager`'s `performTransition` has an in-flight
   * status update (optimistic phase already applied, backend call not yet
   * settled). Must fold into `transitionDisabled` — otherwise the phase
   * flips optimistically, the NEXT transition's control renders enabled,
   * and a click on it hits `usePeriodManager`'s in-flight ref lock and is
   * silently discarded (no request, no error, no feedback). See
   * PMA-COCK-133 (task #82/#84).
   */
  isTransitionPending?: boolean;
}

export const useCockpitTransitionState = ({
  statusOverride,
  currentPhase,
  globalTimeSeconds,
  match,
  isAdmin,
  t,
  isTransitionPending = false,
}: UseCockpitTransitionStateParams) => {
  const transitionGuards = useTransitionGuards({
    statusOverride,
    currentPhase,
    globalTimeSeconds,
    match,
    t,
  });

  // PMA-COCK-028: the condition below is intentionally COMPLETED only. FULLTIME
  // is a distinct, earlier phase (see PeriodPhase in usePeriodManager.ts) that
  // does NOT lock the cockpit — widening this to include FULLTIME is a product
  // decision this fix explicitly does not make (see cockpit-contract.md
  // PMA-COCK-028's v1.221.0 note). `lockReasonCompleted`'s message names the
  // status that actually triggers the lock (was `lockReasonFulltime`, which
  // named the wrong one).
  const cockpitLocked = currentPhase === "COMPLETED";
  const lockReason = cockpitLocked
    ? t("lockReasonCompleted", "Match is Completed. Cockpit is read-only.")
    : undefined;

  const transitionDisabled =
    cockpitLocked ||
    !isAdmin ||
    isTransitionPending ||
    (currentPhase === "FIRST_HALF"
      ? !transitionGuards.canHalftime || !transitionGuards.hasFirstHalfMinimum
      : currentPhase === "HALFTIME"
        ? !transitionGuards.canSecondHalf
        : currentPhase === "FIRST_HALF_EXTRA_TIME"
          ? !transitionGuards.hasExtraFirstHalfMinimum
          : currentPhase === "SECOND_HALF"
            ? !transitionGuards.canFulltime ||
              !transitionGuards.hasSecondHalfMinimum
            : currentPhase === "SECOND_HALF_EXTRA_TIME"
              ? !transitionGuards.hasExtraSecondHalfMinimum
              : false);

  // PMA-COCK-200 (UC2): the Penalties controls — both entry points, direct
  // from FULLTIME and from SECOND_HALF_EXTRA_TIME — take their own disabled
  // input rather than sharing the single `transitionDisabled` boolean
  // above. They are disabled only by what is genuinely theirs (cockpit
  // locked, not admin, or a transition already in flight) and deliberately
  // never by `hasExtraSecondHalfMinimum` / `hasSecondHalfMinimum` — folding
  // either in here would silently reintroduce the regulation-minute guard
  // this UC exempts Penalties from (locked decision 2, 2026-09-16).
  const penaltiesTransitionDisabled =
    cockpitLocked || !isAdmin || isTransitionPending;

  const transitionReason = cockpitLocked
    ? lockReason
    : !isAdmin
      ? t(
          "adminOnlyTransitions",
          "Admin only: match status changes are locked.",
        )
      : currentPhase === "FIRST_HALF" &&
          (!transitionGuards.canHalftime ||
            !transitionGuards.hasFirstHalfMinimum)
        ? !transitionGuards.hasFirstHalfMinimum
          ? transitionGuards.minimumFirstHalfReason
          : transitionGuards.transitionGuardMessage
        : currentPhase === "HALFTIME" && !transitionGuards.canSecondHalf
          ? transitionGuards.transitionGuardMessage
          : currentPhase === "FIRST_HALF_EXTRA_TIME" &&
              !transitionGuards.hasExtraFirstHalfMinimum
            ? transitionGuards.minimumExtraFirstHalfReason
            : currentPhase === "SECOND_HALF" &&
                (!transitionGuards.canFulltime ||
                  !transitionGuards.hasSecondHalfMinimum)
              ? !transitionGuards.hasSecondHalfMinimum
                ? transitionGuards.minimumSecondHalfReason
                : transitionGuards.transitionGuardMessage
              : currentPhase === "SECOND_HALF_EXTRA_TIME" &&
                  !transitionGuards.hasExtraSecondHalfMinimum
                ? transitionGuards.minimumExtraSecondHalfReason
                : undefined;

  return {
    ...transitionGuards,
    cockpitLocked,
    lockReason,
    transitionDisabled,
    penaltiesTransitionDisabled,
    transitionReason,
  };
};
